import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";

const DISMISSED_STORAGE_KEY = "vc_rel_contacto_dismissed";

function digitsOnly(v) {
  return String(v || "").replace(/\D/g, "");
}

function telefonoListo(v) {
  return digitsOnly(v).length >= 9;
}

function emailListo(v) {
  const em = String(v || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
}

function buildKey(telefono, email, excludeId) {
  const tel = telefonoListo(telefono) ? digitsOnly(telefono).slice(-9) : "";
  const em = emailListo(email) ? String(email).trim().toLowerCase() : "";
  return `${tel}|${em}|${excludeId || ""}`;
}

/** Misma huella que el backend (entity:id:etapa:ref). */
function fingerprintMatches(matches) {
  const parts = (Array.isArray(matches) ? matches : [])
    .map((m) => {
      if (!m || typeof m !== "object") return "";
      const type = String(m.entity_type || "").trim().toLowerCase();
      const id = String(m.id || "").trim().toLowerCase();
      const etapa = String(m.etapa || "").trim().toLowerCase();
      const ref = (
        Array.isArray(m.refs) && m.refs[0] != null
          ? String(m.refs[0])
          : m.referencia != null
            ? String(m.referencia)
            : ""
      )
        .trim()
        .toLowerCase();
      return `${type}:${id}:${etapa}:${ref}`;
    })
    .filter(Boolean)
    .sort();
  return parts.join("|");
}

function loadDismissedMap() {
  try {
    const raw = sessionStorage.getItem(DISMISSED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    // Ampliar: formato nuevo { key: fingerprint }. Compat con array de keys.
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
    if (Array.isArray(parsed)) {
      const map = {};
      parsed.forEach((k) => {
        if (k) map[String(k)] = "";
      });
      return map;
    }
    return {};
  } catch {
    return {};
  }
}

function saveDismissedMap(map) {
  try {
    const keys = Object.keys(map || {});
    const slim = {};
    keys.slice(-80).forEach((k) => {
      slim[k] = map[k];
    });
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(slim));
  } catch {
    /* noop */
  }
}

async function logRelacionEvento(payload) {
  try {
    await api.post("/captaciones/relacion-contacto-evento", payload);
  } catch (err) {
    console.warn("[relacion-contacto] log:", err?.message || err);
  }
}

function clearDismissedSinCaptacion() {
  const map = loadDismissedMap();
  const next = {};
  Object.keys(map).forEach((k) => {
    const parts = String(k || "").split("|");
    const ex = parts[2] || "";
    if (/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(ex)) {
      next[k] = map[k];
    }
  });
  saveDismissedMap(next);
}

/**
 * Comprueba teléfono/email en el CRM (debounce) y expone matches para el modal de aviso.
 * Tras Continuar, silencia esa misma huella de coincidencias.
 * Si cambian las coincidencias (p. ej. compañera con Visita Concertada), vuelve a avisar.
 * Cada mostrar/Continuar/Descartar queda auditado en servidor.
 */
export function useCaptacionRelacionContacto({
  excludeCaptacionId = null,
  excludeContactoId = null,
  debounceMs = 300,
  origen = "crm",
  /** 'propietario' → solo Visita/Para Revisar + propietarios de propiedades */
  modo = null,
} = {}) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [canal, setCanal] = useState("telefono");
  const pendingKeyRef = useRef("");
  const pendingCtxRef = useRef(null);
  const timerRef = useRef(null);
  const seqRef = useRef(0);
  const excludeRef = useRef({ captacion: excludeCaptacionId, contacto: excludeContactoId });
  excludeRef.current = { captacion: excludeCaptacionId, contacto: excludeContactoId };
  const modoRef = useRef(modo);
  modoRef.current = modo;

  const isDismissed = useCallback((key, fingerprint) => {
    if (!key) return false;
    const map = loadDismissedMap();
    if (!(key in map)) return false;
    // Sin huella nueva: cualquier Continuar previo de esa key (sesión).
    if (fingerprint == null || fingerprint === undefined) return true;
    return String(map[key] || "") === String(fingerprint || "");
  }, []);

  const markDismissed = useCallback((key, fingerprint) => {
    if (!key) return;
    const map = loadDismissedMap();
    map[key] = fingerprint != null ? String(fingerprint) : "";
    saveDismissedMap(map);
  }, []);

  const unmarkDismissed = useCallback((key) => {
    if (!key) return;
    const map = loadDismissedMap();
    if (key in map) {
      delete map[key];
      saveDismissedMap(map);
    }
  }, []);

  const logEvento = useCallback((evento, ctx) => {
    const c = ctx || pendingCtxRef.current || {};
    void logRelacionEvento({
      evento,
      telefono: c.telefono || null,
      email: c.email || null,
      captacion_id: c.captacion_id || excludeRef.current.captacion || null,
      origen,
      canal_aviso: c.canal || null,
      match_key: c.key || pendingKeyRef.current || null,
      matches: Array.isArray(c.matches) ? c.matches : [],
    });
  }, [origen]);

  const dismiss = useCallback(() => {
    const ctx = pendingCtxRef.current;
    const fp = fingerprintMatches(ctx?.matches);
    logEvento("continuar", ctx);
    if (pendingKeyRef.current) markDismissed(pendingKeyRef.current, fp);
    pendingKeyRef.current = "";
    pendingCtxRef.current = null;
    setOpen(false);
  }, [logEvento, markDismissed]);

  const dismissDiscard = useCallback(() => {
    const ctx = pendingCtxRef.current;
    const fp = fingerprintMatches(ctx?.matches);
    logEvento("descartar", ctx);
    if (pendingKeyRef.current) markDismissed(pendingKeyRef.current, fp);
    pendingKeyRef.current = "";
    pendingCtxRef.current = null;
    setOpen(false);
  }, [logEvento, markDismissed]);

  /** Cierra el modal de aviso sin registrar Continuar (p. ej. al cerrar el formulario). */
  const closeQuiet = useCallback(() => {
    pendingKeyRef.current = "";
    pendingCtxRef.current = null;
    setOpen(false);
    setMatches([]);
  }, []);

  const showMatches = useCallback((list, canalHint, key, ctx = {}) => {
    if (!Array.isArray(list) || !list.length) return;
    const fp = fingerprintMatches(list);
    if (key && isDismissed(key, fp)) return;
    pendingKeyRef.current = key || "";
    const fullCtx = {
      ...ctx,
      key: key || "",
      canal: canalHint || "telefono",
      matches: list,
      captacion_id: ctx.captacion_id || excludeRef.current.captacion || null,
    };
    pendingCtxRef.current = fullCtx;
    logEvento("aviso_mostrado", fullCtx);
    setMatches(list);
    setCanal(canalHint || "telefono");
    setOpen(true);
  }, [isDismissed, logEvento]);

  const buildParams = useCallback((telefono, email, overrides = {}) => {
    const telOk = telefonoListo(telefono);
    const emOk = emailListo(email);
    const params = {};
    if (telOk) params.telefono = String(telefono).trim();
    if (emOk) params.email = String(email).trim();
    const exCap = overrides.excludeCaptacionId ?? excludeRef.current.captacion;
    const exCont = overrides.excludeContactoId ?? excludeRef.current.contacto;
    if (exCap) params.exclude_captacion_id = exCap;
    if (exCont) params.exclude_contacto_id = exCont;
    const modoVal = overrides.modo ?? modoRef.current;
    if (modoVal) params.modo = modoVal;
    return {
      params,
      telOk,
      emOk,
      key: buildKey(telefono, email, `${exCap || ""}:${exCont || ""}:${modoVal || ""}`),
      telefono: telOk ? String(telefono).trim() : null,
      email: emOk ? String(email).trim() : null,
      captacion_id: exCap || null,
    };
  }, []);

  const applyServerResult = useCallback((data, built, key) => {
    const { telOk, emOk } = built;
    if (data?.dismissed) {
      markDismissed(key, data.matches_fingerprint || "");
      return [];
    }
    const list = data?.matches || [];
    if (!list.length) return [];
    const fp = data.matches_fingerprint || fingerprintMatches(list);
    // Misma huella que al Continuar → no reabrir en esta sesión.
    if (isDismissed(key, fp)) return [];
    // Huella distinta (o sin dismiss) → avisar.
    const canalHint = telOk && emOk ? "ambos" : telOk ? "telefono" : "email";
    showMatches(list, canalHint, key, {
      telefono: built.telefono,
      email: built.email,
      captacion_id: built.captacion_id,
    });
    return list;
  }, [isDismissed, markDismissed, showMatches]);

  const check = useCallback((telefono, email) => {
    const built = buildParams(telefono, email);
    const { params, telOk, emOk, key } = built;
    if (!telOk && !emOk) return;
    // Formulario nuevo: tras Continuar no re-preguntar en el mismo tecleo.
    // Con captación (ficha abierta): el servidor decide por fingerprint.
    if (!built.captacion_id && isDismissed(key)) return;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      try {
        const { data } = await api.get("/captaciones/buscar-relacion-contacto", { params });
        if (seq !== seqRef.current) return;
        applyServerResult(data, built, key);
      } catch (err) {
        console.warn("[relacion-contacto]", err?.message || err);
      }
    }, debounceMs);
  }, [applyServerResult, buildParams, debounceMs, isDismissed]);

  const checkNow = useCallback(async (telefono, email, opts = {}) => {
    const built = buildParams(telefono, email, opts);
    const { params, telOk, emOk, key } = built;
    if (!telOk && !emOk) return [];
    const force = opts.force === true;
    // force (abrir ficha): siempre consultar servidor; fingerprint manda.
    if (!force && !built.captacion_id && isDismissed(key)) return [];
    try {
      const { data } = await api.get("/captaciones/buscar-relacion-contacto", { params });
      return applyServerResult(data, built, key);
    } catch (err) {
      console.warn("[relacion-contacto]", err?.message || err);
      return [];
    }
  }, [applyServerResult, buildParams, isDismissed]);

  const showFromServer = useCallback((list, telefono, email) => {
    if (!Array.isArray(list) || !list.length) return;
    const built = buildParams(telefono, email);
    const fp = fingerprintMatches(list);
    if (isDismissed(built.key, fp)) return;
    const canalHint = built.telOk && built.emOk ? "ambos" : built.telOk ? "telefono" : "email";
    showMatches(list, canalHint, built.key, {
      telefono: built.telefono,
      email: built.email,
      captacion_id: built.captacion_id,
    });
  }, [buildParams, isDismissed, showMatches]);

  const resetSeen = useCallback(() => {
    // Nueva precaptación / formulario en blanco: avisar otra vez si no hay captación vinculada.
    if (!excludeRef.current.captacion) clearDismissedSinCaptacion();
    pendingKeyRef.current = "";
    pendingCtxRef.current = null;
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return {
    check,
    checkNow,
    showFromServer,
    resetSeen,
    dismiss,
    dismissDiscard,
    closeQuiet,
    open,
    matches,
    canal,
  };
}

export { telefonoListo, emailListo, fingerprintMatches };
